/**
 * Plain JavaScript chat box UI
 * - Draggable and resizable window
 * - Tabbed interface for filtering messages
 * - Transparent, scrollable message list
 */

import type { SpaceTimeClient } from '../spacetime/SpaceTimeClient';
import type { ChatMessage as SocialChatMessage } from '../spacetime/modules/SocialModule';
import type { EventHub } from '../events/EventHub';
import type { GameState } from '../model/gamestate/GameState';
import { parseAndExecute, type CommandContext } from './chatCommands';
import { setServerCommandDefinitions } from './commands';
import { t } from '../i18n';
import { windowId, windowRegistry } from './windowing';
import { buildChatTabConfigs, interpretChatChannel, type ChatChannelInfo as ChannelInfo, type ChatTab as Tab } from './chatBoxModel';
import { ActionTooltip } from './ActionTooltip';
import {
  buildCodexChatToken,
  CODEX_CHAT_INSERT_EVENT,
  parseCodexMessageParts,
  resolveCodexPreviewRow,
  type CodexChatLinkPayload,
} from './codexChatLinks';
import { createCodexRowElement } from './codexTabRenderer';
import { showSpeechBubble } from '../chat/speechBubbles';

const MIN_WIDTH = 300;
const MIN_HEIGHT = 200;
const MAX_CHAT_LENGTH = 128;
const CHAT_INPUT_HISTORY_LIMIT = 50;

const CHANNEL_STYLES: Record<ChannelInfo['kind'] | 'GLOBAL', { tagKey: string; color: string }> = {
  SYSTEM: { tagKey: 'chat.tags.system', color: '#fbbf24' },
  MAP: { tagKey: 'chat.tags.map', color: '#ffffff' },
  GUILD: { tagKey: 'chat.tags.guild', color: '#cbd5e1' },
  FRIEND: { tagKey: 'chat.tags.friends', color: '#60a5fa' },
  WHISPER: { tagKey: 'chat.tags.whisper', color: '#d946ef' },
  GLOBAL: { tagKey: 'chat.tags.system', color: '#fbbf24' },
};


interface ChatMessage {
  messageId: number | string;
  senderId: string;
  senderName?: string;
  channel: unknown;
  text: string;
  timestamp: Date | unknown;
}
export interface ChatBoxHandle {
  show(): void;
  hide(): void;
  isVisible(): boolean;
  destroy(): void;
}

export function createChatBox(
  gameState: GameState,
  spaceTimeClient: SpaceTimeClient,
  _eventHub?: EventHub,
): ChatBoxHandle {
  let destroyed = false;
  const TAB_CONFIGS = buildChatTabConfigs(t);
  // State
  let messages: ChatMessage[] = [];
  const seenMessageIds = new Set<number | string>();
  let hasHydratedInitialChatFeed = false;
  let activeTab: Tab = 'ALL';
  const defaultPosition = { x: 16, y: window.innerHeight - 300 };
  const defaultSize = { width: 350, height: 250 };
  let currentGuildName: string | null = null;
  let lastWhisperTarget: string | null = null;
  let lastWhisperSender: string | null = null;
  let chatInputHistory: string[] = [];
  let chatInputHistoryIndex = -1;
  let chatInputDraft = '';
  const codexTooltip = new ActionTooltip();

  // Command context for chat commands
  const commandContext: CommandContext = {
    gameState,
    spaceTimeClient,
    getLastWhisperTarget: () => lastWhisperTarget,
    setLastWhisperTarget: (target) => { lastWhisperTarget = target; },
    getLastWhisperSender: () => lastWhisperSender,
    setLastWhisperSender: (sender) => { lastWhisperSender = sender; },
    showLocalMessage: (text) => {
      // Show a local-only message (e.g., for errors)
      const errorMsg: ChatMessage = {
        messageId: `local-${Date.now()}`,
        senderId: t('chat.systemName'),
        senderName: t('chat.systemName'),
        channel: { tag: 'System' },
        text,
        timestamp: new Date(),
      };
      messages.push(errorMsg);
      renderMessages();
    },
  };

  // Create container
  const container = document.createElement('div');
  container.className = 'chat-box-container window-container';
  Object.assign(container.style, {
    left: `${defaultPosition.x}px`,
    top: `${defaultPosition.y}px`,
    width: `${defaultSize.width}px`,
    height: `${defaultSize.height}px`,
  });

  // Create header
  const header = document.createElement('div');
  header.className = 'chat-box-header window-header';

  const headerTitle = document.createElement('div');
  headerTitle.className = 'window-header-title';
  headerTitle.textContent = t('chat.title');

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.textContent = '×';
  closeButton.title = 'Hide chat';
  Object.assign(closeButton.style, {
    marginLeft: 'auto',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#9ca3af',
    fontSize: '1.2rem',
    cursor: 'pointer',
    lineHeight: '1',
    zIndex: '20',
  });

  const resizeHandle = document.createElement('div');
  resizeHandle.className = 'window-resize-handle';
  resizeHandle.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" style="pointer-events: none;"><path d="M0 0 L10 0 L10 10 Z" fill="#9ca3af" /></svg>';

  header.appendChild(headerTitle);
  header.appendChild(closeButton);
  header.appendChild(resizeHandle);

  // Create tabs
  const tabContainer = document.createElement('div');
  tabContainer.className = 'chat-box-tabs';
  Object.assign(tabContainer.style, {
    display: 'flex',
    background: 'rgba(0, 0, 0, 0.2)',
    borderBottom: '1px solid #2a3142',
  });

  const tabs: Tab[] = ['ALL', 'MAP', 'FRIENDS', 'GUILD', 'SYSTEM', 'WHISPER'];
  const tabElements = new Map<Tab, HTMLDivElement>();

  tabs.forEach((tab) => {
    const tabEl = document.createElement('div');
    tabEl.textContent = TAB_CONFIGS[tab].label;
    tabEl.className = 'chat-box-tab';
    Object.assign(tabEl.style, {
      padding: '6px 12px',
      fontSize: '0.7rem',
      color: '#6b7280',
      cursor: 'pointer',
      fontWeight: '600',
      transition: 'all 0.2s',
      borderBottom: '2px solid transparent',
    });

    tabEl.addEventListener('click', () => setActiveTab(tab));
    tabElements.set(tab, tabEl);
    tabContainer.appendChild(tabEl);
  });

  // Create message list
  const messageList = document.createElement('div');
  messageList.className = 'chat-box-messages';
  Object.assign(messageList.style, {
    flex: '1',
    overflowY: 'auto',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    scrollbarWidth: 'thin',
  });

  // Create input form
  const inputForm = document.createElement('form');
  inputForm.className = 'chat-box-input-form';
  Object.assign(inputForm.style, {
    padding: '10px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderTop: '1px solid #2a3142',
  });

  const input = document.createElement('input');
  input.type = 'text';
  input.maxLength = MAX_CHAT_LENGTH;
  input.placeholder = TAB_CONFIGS[activeTab].placeholder;
  Object.assign(input.style, {
    width: '100%',
    background: '#1a1f26',
    border: '1px solid #334155',
    borderRadius: '6px',
    color: '#f1f5f9',
    padding: '8px 12px',
    fontSize: '0.85rem',
    outline: 'none',
  });

  inputForm.appendChild(input);

  // Assemble
  container.appendChild(header);
  container.appendChild(tabContainer);
  container.appendChild(messageList);
  container.appendChild(inputForm);
  document.body.appendChild(container);
  const chatWindow = windowRegistry.register({
    id: windowId('chat-box', 'main'),
    container,
    header,
    resizeHandle,
    closeButton,
    consumeEscape: false,
    minWidth: MIN_WIDTH,
    minHeight: MIN_HEIGHT,
    defaultPosition,
    defaultSize,
    defaultTab: 'ALL',
    allowedTabs: ['ALL', 'MAP', 'FRIENDS', 'GUILD', 'SYSTEM', 'WHISPER'],
    stateChangeDebounceMs: 500,
  });

  // Functions
  function setActiveTab(tab: Tab): void {
    activeTab = tab;
    chatWindow.setActiveTab(tab);
    const config = TAB_CONFIGS[tab];
    input.placeholder = config.placeholder;
    // Special handling for WHISPER tab enabling
    if (tab === 'WHISPER') {
      input.disabled = false; // Always allow typing commands
    } else {
      input.disabled = config.getSendChannel({ gameState, currentGuildName }) === null;
    }
    input.style.opacity = input.disabled ? '0.5' : '1';
    input.style.cursor = input.disabled ? 'not-allowed' : 'text';

    // Update tab styles
    tabElements.forEach((el, t) => {
      if (t === tab) {
        Object.assign(el.style, {
          color: '#60a5fa',
          borderBottom: '2px solid #60a5fa',
          background: 'rgba(96, 165, 250, 0.1)',
        });
      } else {
        Object.assign(el.style, {
          color: '#6b7280',
          borderBottom: '2px solid transparent',
          background: 'transparent',
        });
      }
    });

    renderMessages();
  }

  function createMessageElement(msg: ChatMessage): HTMLDivElement {
    const messageDiv = document.createElement('div');
    messageDiv.className = 'chat-message';
    Object.assign(messageDiv.style, {
      fontSize: '0.85rem',
      color: '#e0e7ff',
      lineHeight: '1.4',
      whiteSpace: 'pre-line',
    });

    // Add source tag only in ALL tab
    if (activeTab === 'ALL') {
      const info = interpretChatChannel(msg.channel);
      const style = CHANNEL_STYLES[info.kind] ?? CHANNEL_STYLES.GLOBAL;

      const sourceSpan = document.createElement('span');
      sourceSpan.textContent = `${t(style.tagKey)} `;
      Object.assign(sourceSpan.style, {
        color: style.color,
        fontWeight: 'bold',
        marginRight: '4px',
      });
      messageDiv.appendChild(sourceSpan);
    }

    const info = interpretChatChannel(msg.channel);
    if (info.kind !== 'SYSTEM') {
      const sender = document.createElement('span');
      const name = msg.senderName || msg.senderId;
      sender.textContent = t('chat.sender', { name });
      Object.assign(sender.style, {
        color: '#94a3b8',
        fontWeight: '600',
        marginRight: '6px',
      });
      messageDiv.appendChild(sender);
      messageDiv.appendChild(document.createTextNode(' '));
      appendMessageTextContent(messageDiv, msg.text);
    } else {
      appendMessageTextContent(messageDiv, msg.text);
    }

    return messageDiv;
  }

  function addMessage(msg: ChatMessage): void {
    const messageDiv = createMessageElement(msg);
    messageList.appendChild(messageDiv);
    messageList.scrollTop = messageList.scrollHeight;
  }



  function renderMessages(): void {
    messageList.innerHTML = '';

    const filtered = messages.filter((msg) => {
      const config = TAB_CONFIGS[activeTab];
      const info = interpretChatChannel(msg.channel);
      return config.filter(info);
    });

    filtered.forEach((msg) => {
      messageList.appendChild(createMessageElement(msg));
    });

    messageList.scrollTop = messageList.scrollHeight;
  }

  function handleSendMessage(e: Event): void {
    e.preventDefault();
    const text = input.value.trim();
    if (!text) return;

    if (text.length > MAX_CHAT_LENGTH) {
      commandContext.showLocalMessage(
        t('chat.errors.messageTooLong', { len: text.length, max: MAX_CHAT_LENGTH })
      );
      return;
    }

    if (chatInputHistory[chatInputHistory.length - 1] !== text) {
      chatInputHistory.push(text);
      if (chatInputHistory.length > CHAT_INPUT_HISTORY_LIMIT) {
        chatInputHistory = chatInputHistory.slice(chatInputHistory.length - CHAT_INPUT_HISTORY_LIMIT);
      }
    }
    chatInputHistoryIndex = -1;
    chatInputDraft = '';

    // Check for commands first
    if (text.startsWith('/')) {
      const result = parseAndExecute(text, commandContext);
      if (result?.handled) {
        if (result.error) {
          commandContext.showLocalMessage(t('chat.errors.command', { error: result.error }));
        } else if (result.channel && result.text) {
          spaceTimeClient.social.sendChat(result.text, result.channel as any);
        }
        input.value = '';
        return;
      }
    }

    const config = TAB_CONFIGS[activeTab];
    let channel = config.getSendChannel({ gameState, currentGuildName });

    // Special handling for WHISPER tab if we want to support replying to last whisper
    if (activeTab === 'WHISPER' && !channel && lastWhisperTarget) {
      channel = { tag: 'Whisper', value: lastWhisperTarget };
    }

    if (!channel) {
      if (activeTab === 'GUILD' && !currentGuildName) {
        commandContext.showLocalMessage(t('chat.errors.noGuild'));
      } else if (activeTab === 'SYSTEM') {
        commandContext.showLocalMessage(t('chat.errors.systemReadOnly'));
      } else {
        console.warn('Unable to determine chat channel for tab', activeTab);
      }
      return;
    }

    spaceTimeClient.social.sendChat(text, channel as any);
    input.value = '';
  }

  function handleChatWheel(e: WheelEvent): void {
    e.stopPropagation();
    e.preventDefault();

    if (container.hidden) {
      return;
    }

    messageList.scrollTop += e.deltaY;
  }

  function appendMessageTextContent(containerEl: HTMLElement, text: string): void {
    const skills = gameState.getKnownSkills();
    const items = spaceTimeClient.catalogs.getCachedItemCatalog()?.entries ?? [];
    const mobs = spaceTimeClient.catalogs.getCachedMobCatalog()?.entries ?? [];
    for (const part of parseCodexMessageParts(text)) {
      if (part.kind === 'text') {
        containerEl.appendChild(document.createTextNode(part.text));
        continue;
      }
      const row = resolveCodexPreviewRow(part.payload, { skills, items, mobs });
      const chip = document.createElement('span');
      chip.className = 'chat-codex-link';
      chip.textContent = `[${row?.title || part.payload.label || part.payload.value}]`;
      Object.assign(chip.style, {
        color: '#93c5fd',
        cursor: 'pointer',
        fontWeight: '600',
      });
      chip.addEventListener('mouseenter', () => {
        if (row) {
          codexTooltip.showNodeAbove(createCodexRowElement(row), chip);
        }
      });
      chip.addEventListener('mouseleave', () => codexTooltip.hide());
      chip.addEventListener('mousedown', () => codexTooltip.hide());
      containerEl.appendChild(chip);
    }
  }

  function insertAtCursor(tokenText: string): void {
    const selectionStart = input.selectionStart ?? input.value.length;
    const selectionEnd = input.selectionEnd ?? input.value.length;
    const prefix = input.value.slice(0, selectionStart);
    const suffix = input.value.slice(selectionEnd);
    const needsLeadingSpace = prefix.length > 0 && !/\s$/.test(prefix);
    const needsTrailingSpace = suffix.length > 0 && !/^\s/.test(suffix);
    const insertion = `${needsLeadingSpace ? ' ' : ''}${tokenText}${needsTrailingSpace ? ' ' : ''}`;
    input.value = `${prefix}${insertion}${suffix}`;
    const nextCursor = prefix.length + insertion.length;
    input.focus();
    input.setSelectionRange(nextCursor, nextCursor);
  }

  const handleCodexInsert = (event: Event): void => {
    const detail = (event as CustomEvent<CodexChatLinkPayload>).detail;
    if (!detail) return;
    insertAtCursor(buildCodexChatToken(detail));
  };

  // Event listeners
  closeButton.addEventListener('mousedown', (e) => {
    e.preventDefault();
    e.stopPropagation();
  });
  closeButton.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    chatWindow.setWindowState('closed');
  });
  inputForm.addEventListener('submit', handleSendMessage);
  container.addEventListener('wheel', handleChatWheel, { passive: false });
  window.addEventListener(CODEX_CHAT_INSERT_EVENT, handleCodexInsert as EventListener);

  // Prevent input from triggering game controls
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();

    if (e.key !== 'ArrowUp') {
      return;
    }

    if (chatInputHistory.length === 0) {
      return;
    }

    e.preventDefault();

    if (chatInputHistoryIndex === -1) {
      chatInputDraft = input.value;
      chatInputHistoryIndex = chatInputHistory.length - 1;
    } else if (chatInputHistoryIndex > 0) {
      chatInputHistoryIndex -= 1;
    }

    input.value = chatInputHistory[chatInputHistoryIndex] ?? chatInputDraft;
    const cursor = input.value.length;
    input.setSelectionRange(cursor, cursor);
  });

  // Subscribe to chat updates
  let chatSubscription: { unsubscribe: () => void } | undefined;
  const assignSubscription = (
    setter: (subscription: { unsubscribe: () => void } | undefined) => void,
    subscription: { unsubscribe: () => void }
  ): void => {
    if (destroyed) {
      subscription.unsubscribe();
      return;
    }
    setter(subscription);
  };


  spaceTimeClient.social
    .subscribeToChat((msgs: SocialChatMessage[]) => {
      if (destroyed) return;
      // Track incoming whispers for /r command
      const myPlayerId = spaceTimeClient.getPlayerId();
      if (myPlayerId) {
        for (const msg of msgs) {
          const info = interpretChatChannel(msg.channel);
          if (info.kind === 'WHISPER' && msg.senderId !== myPlayerId) {
            lastWhisperSender = msg.senderId;
          }
        }
      }
      for (const msg of msgs) {
        if (seenMessageIds.has(msg.messageId)) {
          continue;
        }
        seenMessageIds.add(msg.messageId);
        if (!hasHydratedInitialChatFeed) {
          continue;
        }
        const info = interpretChatChannel(msg.channel);
        if (info.kind === 'SYSTEM') {
          continue;
        }
        if (myPlayerId && msg.senderId === myPlayerId) {
          showSpeechBubble('__self__', msg.text);
          continue;
        }
        if (msg.senderId) {
          showSpeechBubble(msg.senderId, msg.text);
        }
      }
      hasHydratedInitialChatFeed = true;
      if (seenMessageIds.size > msgs.length + 64) {
        const liveIds = new Set(msgs.map((msg) => msg.messageId));
        for (const seenId of [...seenMessageIds]) {
          if (!liveIds.has(seenId)) {
            seenMessageIds.delete(seenId);
          }
        }
      }
      // Map to local ChatMessage type
      messages = msgs.map((m) => ({
        messageId: m.messageId,
        senderId: m.senderId,
        senderName: m.senderName,
        channel: m.channel,
        text: m.text,
        timestamp: m.timestamp
      }));
      renderMessages();
    })
    .then((sub) => {
      assignSubscription((subscription) => {
        chatSubscription = subscription;
      }, sub);
    })
    .catch((err) => {
      console.error('[ChatBox] Subscription failed', err);
    });

  // Subscribe to guild updates
  let guildSubscription: { unsubscribe: () => void } | undefined;
  let commandSubscription: { unsubscribe: () => void } | undefined;


  spaceTimeClient.social
    .subscribeToChatCommands((definitions) => {
      if (destroyed) return;
      setServerCommandDefinitions(definitions);
    })
    .then((sub) => {
      assignSubscription((subscription) => {
        commandSubscription = subscription;
      }, sub);
    })
    .catch((err) => {
      console.error('[ChatBox] Command subscription failed', err);
    });

  spaceTimeClient.social
    .subscribeToGuild((guild) => {
      if (destroyed) return;
      currentGuildName = guild?.guildName ?? null;
      // If we are currently in GUILD tab and left guild, maybe switch tab? 
      // For now just updating state is enough.
    })
    .then((sub) => {
      assignSubscription((subscription) => {
        guildSubscription = subscription;
      }, sub);
    })
    .catch((err) => {
      console.error('[ChatBox] Guild subscription failed', err);
    });

  // Initialize active tab styles
  setActiveTab(chatWindow.getActiveTab() as Tab);
  const restoredWindowState = chatWindow.getWindowState();
  if (restoredWindowState === 'closed') {
    container.hidden = true;
    container.style.display = 'none';
  } else {
    chatWindow.setWindowState('open');
  }

  // Cleanup function
  return {
    show() {
      chatWindow.setWindowState('open');
    },
    hide() {
      chatWindow.setWindowState('closed');
    },
    isVisible() {
      return chatWindow.getWindowState() !== 'closed';
    },
    destroy() {
      destroyed = true;
      chatSubscription?.unsubscribe();
      guildSubscription?.unsubscribe();
      commandSubscription?.unsubscribe();
      container.removeEventListener('wheel', handleChatWheel);
      window.removeEventListener(CODEX_CHAT_INSERT_EVENT, handleCodexInsert as EventListener);
      codexTooltip.hide();
      chatWindow.destroy();
      container.remove();
    },
  };
}
